import { FC, KeyboardEvent, useMemo, useState } from 'react';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { Button } from 'components/Core/Button';
import { IKeywordItem } from 'stores/categories/types';
import { productCategoryKeywordsFormTranslations } from './translations';
import { IProductCategoryKeywordsFormProps, MatchType, ProductCategoryKeywordsFormTransFields } from './types';
import styles from './productCategoryKeywordsForm.module.scss';

// Drops everything that is not a letter/number/whitespace. Built via `new RegExp`
// (not a literal) because the `u` flag + \p{} unicode-property escapes are ES2018
// features and the literal trips the es5 target check — the engine supports them at runtime.
const NON_WORD = new RegExp('[^\\p{L}\\p{N}\\s]+', 'gu');

/**
 * Mirror of the search-side normalization (mobile-api CategoryKeywordService.normalize):
 * lowercase, strip Latin diacritics, drop punctuation, collapse whitespace.
 * Used here only to predict HOW a keyword will match — the raw keyword is what gets saved.
 */
const normalize = (s: string): string =>
  (s ?? '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(NON_WORD, ' ').replace(/\s+/g, ' ').trim();

const matchTypeOf = (keyword: string): MatchType => {
  const n = normalize(keyword);
  if (!n) return 'invalid';
  // single token -> exact whole-word match; multi-word -> phrase substring match
  return n.includes(' ') ? 'phrase' : 'word';
};

type Trans = (key: ProductCategoryKeywordsFormTransFields, opts?: Record<string, string>) => string;

interface RowProps {
  item: IKeywordItem;
  owner?: string;
  isLoading: boolean;
  t: Trans;
  onUpdate: (id: string, payload: { name?: string; weight?: number }) => void;
  onDelete: (id: string) => void;
}

// A single editable keyword row: name + weight commit on blur/Enter (only when changed).
// Rendered as `display: contents` so its cells land in the parent grid columns.
const KeywordRow: FC<RowProps> = ({ item, owner, isLoading, t, onUpdate, onDelete }) => {
  const [name, setName] = useState<string>(item.name);
  const [weight, setWeight] = useState<string>(String(item.weight ?? 0));
  const type = matchTypeOf(name);

  const commitName = () => {
    const next = name.trim();
    if (!next || next === item.name) {
      setName(item.name);
      return;
    }
    onUpdate(item.id, { name: next });
  };

  const commitWeight = () => {
    const parsed = Math.max(0, Math.trunc(Number(weight)));
    if (!Number.isFinite(parsed) || parsed === item.weight) {
      setWeight(String(item.weight ?? 0));
      return;
    }
    onUpdate(item.id, { weight: parsed });
  };

  // Enter just blurs — the field's onBlur handler performs the actual commit.
  const onEnterBlur = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <li className={styles.row}>
      <input
        className={styles.input}
        type='text'
        value={name}
        disabled={isLoading}
        onChange={(e) => setName(e.target.value)}
        onBlur={commitName}
        onKeyDown={onEnterBlur}
      />
      <div className={styles.badges}>
        <span
          className={`${styles.badge} ${
            type === 'phrase' ? styles.badgePhrase : type === 'word' ? styles.badgeWord : styles.badgeInvalid
          }`}>
          {type === 'phrase' ? t('badgePhrase') : type === 'word' ? t('badgeWord') : t('badgeInvalid')}
        </span>
        {owner && (
          <span className={`${styles.badge} ${styles.badgeShared}`} title={t('crossCategory', { name: owner })}>
            {t('crossCategoryShort')}
          </span>
        )}
      </div>
      <input
        className={`${styles.input} ${styles.inputWeight}`}
        type='number'
        min={0}
        value={weight}
        title={t('weightLabel')}
        disabled={isLoading}
        onChange={(e) => setWeight(e.target.value)}
        onBlur={commitWeight}
        onKeyDown={onEnterBlur}
      />
      <button
        type='button'
        className={styles.rowRemove}
        aria-label={t('remove')}
        disabled={isLoading}
        onClick={() => onDelete(item.id)}>
        ✕
      </button>
    </li>
  );
};

const ProductCategoryKeywordsForm: FC<IProductCategoryKeywordsFormProps> = ({
  keywords,
  onCreate,
  onUpdate,
  onDelete,
  isLoading,
  otherCategories,
}) => {
  const { t } = useMyTranslations<ProductCategoryKeywordsFormTransFields>([
    productCategoryKeywordsFormTranslations.name,
  ]);

  const [draft, setDraft] = useState<string>('');
  const [draftWeight, setDraftWeight] = useState<string>('');

  const seen = useMemo(() => new Set((keywords || []).map((k) => normalize(k.name))), [keywords]);

  // normalized keyword -> name of another category that already uses it
  const foreignOwner = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of otherCategories || []) {
      for (const kw of c.keywords || []) {
        const n = normalize(kw);
        if (n && !m.has(n)) m.set(n, c.categoryName);
      }
    }
    return m;
  }, [otherCategories]);

  const draftIsDuplicate = !!normalize(draft) && seen.has(normalize(draft));
  const draftForeignOwner = normalize(draft) ? foreignOwner.get(normalize(draft)) : undefined;

  const addFromDraft = () => {
    const name = draft.trim();
    if (!normalize(name) || draftIsDuplicate) return;
    const weight = Math.max(0, Math.trunc(Number(draftWeight)) || 0);
    onCreate({ name, weight });
    setDraft('');
    setDraftWeight('');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addFromDraft();
    }
  };

  return (
    <div className={styles.content}>
      <div className={styles.card}>
        <div className={styles.header}>
          <span className={styles.count}>
            {keywords.length} {t('count')}
          </span>
        </div>
        <p className={styles.hint}>{t('hint')}</p>

        <div className={styles.addRow}>
          <input
            className={styles.input}
            type='text'
            value={draft}
            placeholder={t('placeholder')}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={isLoading}
          />
          <input
            className={`${styles.input} ${styles.inputWeight}`}
            type='number'
            min={0}
            value={draftWeight}
            placeholder={t('weightPlaceholder')}
            onChange={(e) => setDraftWeight(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={isLoading}
          />
          <Button
            title={t('add')}
            onClick={addFromDraft}
            isDisabled={isLoading || !normalize(draft) || draftIsDuplicate}
          />
        </div>
        {draftIsDuplicate && <span className={styles.duplicate}>{t('duplicate')}</span>}
        {!draftIsDuplicate && draftForeignOwner && (
          <span className={styles.crossCategory}>{t('crossCategory', { name: draftForeignOwner })}</span>
        )}

        <span className={styles.sectionLabel}>{t('currentLabel')}</span>
        {keywords.length ? (
          <ul className={styles.list}>
            {keywords.map((item) => (
              <KeywordRow
                key={item.id}
                item={item}
                owner={foreignOwner.get(normalize(item.name))}
                isLoading={isLoading}
                t={t}
                onUpdate={onUpdate}
                onDelete={onDelete}
              />
            ))}
          </ul>
        ) : (
          <p className={styles.empty}>{t('empty')}</p>
        )}
      </div>
    </div>
  );
};

export default ProductCategoryKeywordsForm;
